'use client';

import { useState } from 'react';
import Header from '@/components/Header';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { BRAND_FACTS } from '@/config/brandFacts';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    id: 1,
    question: "How does Marathi Vadhu Var Suchak work?",
    answer: "Marathi Vadhu Var Suchak is a Marathi matrimonial platform that connects Marathi Vadhu Var looking for Anuroop Jeevansathi. You can create a profile, browse through other profiles, and connect with potential matches. Our advanced matching algorithm helps find compatible partners based on your preferences and criteria."
  },
  {
    id: 2,
    question: "Is Marathi Vadhu Var Suchak free to use?",
    answer: "Marathi Vadhu Var Suchak offer both free and premium membership options. Free users can create profiles and browse basic information, while premium members get access to advanced features like unlimited messaging, Chatting, detailed profile views including contact numbers and priority customer support."
  },
  {
    id: 3,
    question: "How do I create a profile on Marathi Vadhu Var Suchak?",
    answer: "Creating a profile is simple! Sign up with your email or mobile number OTP verification; fill in your basic details like name, age, location, and preferences. You can also upload photos and add more information about yourself to make your profile more attractive to potential matches."
  },
  {
    id: 4,
    question: "How do I know if someone is genuine?",
    answer: "Marathi Vadhu Var Suchak has a comprehensive verification system in place. Marathi Vadhu Var can verify their matrimony profiles with verified mobile numbers, and email address. Marathi Vadhu Var Suchak also has a team that manually reviews profiles. Still, you have questions report any suspicious behavior."
  },
  {
    id: 5,
    question: "Can I delete my profile?",
    answer: "Yes, you can delete your profile at any time. Go to your account settings and select 'Delete Account'. However, before deleting profile you have to read our legal disclaimer policy."
  },
  {
    id: 6,
    question: "How do I report inappropriate behavior?",
    answer: "If you encounter any inappropriate behavior or suspicious profiles, you can report them using the 'Report' button on their profile or contact Marathi Vadhu Var Suchak support team directly. We take all reports seriously and investigate them promptly."
  },
  {
    id: 7,
    question: "What information should I share in my profile?",
    answer: "Marathi Vadhu Var should share information that helps others understand who you are and what you're looking for. Include your interests, values, education, career, and family background. However, avoid sharing sensitive personal information like financial details or exact addresses."
  },
  {
    id: 8,
    question: "How do I contact customer support?",
    answer: `You can reach Marathi Vadhu Var Suchak customer support team through multiple channels: email at contact@marathivadhuvarsuchak.com, phone at ${BRAND_FACTS.phone}, or through the contact form on our website. We typically respond within 24 hours.`
  },
  {
    id: 9,
    question: "Is my personal information safe?",
    answer: "Yes, Marathi Vadhu Var Suchak takes your privacy and security very seriously. Marathi Vadhu Var Suchak use industry-standard encryption, secure servers, and strict access controls to protect your personal information data. We never share your data with third parties without your explicit consent. For more details, please read our privacy policy."
  },
  {
    id: 10,
    question: "Is Marathi Vadhu Var Suchak having mobile app?",
    answer: "Yes! Marathi Vadhu Var Suchak website is fully responsive and works on all devices including smartphones and tablets. We also have dedicated mobile apps available for both iOS and Android platforms for an enhanced mobile experience."
  }
];

export default function FAQClient() {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (id: number) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <Header variant="fixed" />

      {/* Content Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">
              Frequently Asked Questions
            </h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                Find answers to the most commonly asked questions about Marathi Vadhu Var Suchak. If you don&apos;t find what you&apos;re looking for, please don&apos;t hesitate to contact our support team.
              </p>

              {/* FAQ Items */}
              <div className="space-y-4">
                {faqData.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="text-lg font-medium text-gray-900">
                        {item.question}
                      </span>
                      {openItems.includes(item.id) ? (
                        <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    
                    {openItems.includes(item.id) && (
                      <div className="px-6 pb-4">
                        <div className="border-t border-gray-200 pt-4">
                          <p className="text-gray-600 leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Contact Section */}
              <div className="mt-12 bg-gray-50 p-6 rounded-lg">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  Still have questions?
                </h2>
                <p className="text-gray-600 mb-4">
                  If you couldn&apos;t find the answer you were looking for, our support team is here to help.
                </p>
                <div className="space-y-2 text-gray-700 contact-info">
                  <p><strong>Email:</strong> contact@marathivadhuvarsuchak.com</p>
                  <p><strong>Phone:</strong> +91 8591407273 / 9326013123</p>
                  <p><strong>Response Time:</strong> Within 24 hours</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
